import { InvoiceConfig, QuoteConfig } from '../../types';
import { CompanyInfo } from './letterhead';

/** Firmenstammdaten, wie sie aus dem Benutzerprofil kommen. */
export interface CompanyBase {
    name?: string;
    address?: string;
    logo?: string;
    footerText?: string;
    /** Eigene DIN-Absenderzeile — leer heisst automatisch Name • Anschrift. */
    senderLine?: string;
}

/**
 * Die Briefkopf-Felder, die QuoteConfig und InvoiceConfig gleich benennen.
 *
 * Absichtlich eine eigene Form statt einer der beiden Konfigurationen: der
 * Zusammenbau darunter kennt nur Briefkopf, nicht Angebot oder Rechnung, und
 * kann deshalb nicht fuer eine Belegart anders ausfallen als fuer die andere.
 */
interface LetterheadFields {
    letterheadText?: string;
    letterheadLeftLine?: string;
    letterheadLeftLineFontSize?: number;
    stationeryPage1?: string;
    stationeryFollowing?: string;
    stationeryTopMarginPage1?: number;
    stationeryTopMarginFollowing?: number;
    stationeryBottomMargin?: number;
}

const buildFromLetterhead = (
    company: CompanyBase | undefined,
    letterhead: LetterheadFields | undefined,
    footerFontSize: number | undefined,
): CompanyInfo => ({
    ...(company || {}),
    letterheadText: letterhead?.letterheadText,
    letterheadLeftLine: letterhead?.letterheadLeftLine,
    letterheadLeftLineFontSize: letterhead?.letterheadLeftLineFontSize,
    footerFontSize,
    // Ganzseiten-Briefpapier nur, wenn Seite 1 hinterlegt ist. Ohne page1 gibt
    // es kein Briefpapier — die Folgeseiten allein ergaeben ein Dokument, dessen
    // erste Seite nackt waere.
    stationery: letterhead?.stationeryPage1
        ? {
              page1: letterhead.stationeryPage1,
              following: letterhead.stationeryFollowing,
              topMarginPage1: letterhead.stationeryTopMarginPage1,
              topMarginFollowing: letterhead.stationeryTopMarginFollowing,
              bottomMargin: letterhead.stationeryBottomMargin,
          }
        : undefined,
});

/**
 * Setzt den Firmenkopf fuer die PDF-Erzeugung aus Stammdaten und Briefkopf-
 * Konfiguration zusammen.
 *
 * Dieser Block stand bisher wortgleich in QuoteEditor und QuoteSendModal. Mit
 * dem Rechnungsgenerator waere er ein drittes Mal entstanden — und drei Kopien
 * driften garantiert auseinander, was in der Praxis bedeutet, dass Angebot und
 * Rechnung unterschiedliche Briefkoepfe tragen.
 */
export const buildCompanyForPdf = (
    company: CompanyBase | undefined,
    quoteConfig: QuoteConfig | undefined,
    /**
     * Rechnungs-Stammdaten fuer die Spalten-Fusszeile. Mit ihnen traegt das
     * Angebot denselben vierspaltigen Fuss wie die Rechnung — eine
     * Pflegestelle statt zweier Fusszeilen, die auseinanderdriften. Ohne
     * bleibt die bisherige freie Fusszeile.
     */
    invoiceConfig?: InvoiceConfig,
): CompanyInfo => {
    const footer = composeInvoiceFooter(company, invoiceConfig);
    return {
        ...buildFromLetterhead(company, quoteConfig, quoteConfig?.footerFontSize),
        footerColumns: footer?.columns,
        footerFlexColumn: footer?.flexColumn,
    };
};

/**
 * Derselbe Firmenkopf fuer Rechnungen — auf Wunsch aus eigenen Angaben.
 *
 * Ohne InvoiceConfig.ownLetterhead ist das Ergebnis buchstaeblich das des
 * Angebots: eine Pflegestelle, ein Briefkopf. Der Schalter existiert, weil
 * eine Rechnung weniger Angaben tragen soll als ein Angebot.
 *
 * Bei gesetztem Schalter gelten NUR die Rechnungsfelder, auch die leeren. Ein
 * Rueckfall Feld fuer Feld haette die Trennung wertlos gemacht: die eine
 * Angabe, die auf der Rechnung nicht stehen soll, liesse sich dann nicht
 * entfernen, weil "leer" als "nicht gepflegt" gelesen wuerde.
 *
 * footerFontSize kommt in beiden Faellen aus QuoteConfig — reine Typografie,
 * die zwischen Angebot und Rechnung nicht auseinanderlaufen soll.
 */
/**
 * Vier-Spalten-Fusszeile der Rechnung aus den strukturierten Feldern:
 * Firma/Adresse · Kontakt · Pflichtangaben nach § 14 UStG · Bankverbindung.
 *
 * Auf dem Druckbild standen diese Angaben bisher nur, wenn der Betreiber sie
 * von Hand in den freien Fusszeilentext schrieb — die strukturierten Felder
 * speisten allein die E-Rechnung. Die Spalten schliessen die Luecke: was die
 * Ausstellungs-Validierung verlangt, steht damit garantiert auch auf dem
 * Papier und kann nie vom XML abweichen.
 *
 * Leere Felder entfallen, leere Spalten ebenso. Ohne ein einziges
 * strukturiertes Feld liefert die Funktion undefined — dann bleibt die
 * bisherige zentrierte Fusszeile aus dem freien Text bestehen.
 */
const composeInvoiceFooter = (
    company: CompanyBase | undefined,
    invoiceConfig: InvoiceConfig | undefined,
): { columns: string[][]; flexColumn?: number } | undefined => {
    if (!invoiceConfig) return undefined;
    const t = (v?: string) => v?.trim() || '';

    // Manuelle Fusszeile: die Texte des Betreibers ersetzen die Automatik
    // vollstaendig — Zeile fuer Zeile, ohne Flex-Spalte (die Umbrueche hat er
    // selbst gesetzt). Ganz ohne Inhalt greift wieder die Automatik: der
    // Schalter allein darf die Fusszeile nicht leeren.
    if (invoiceConfig.footerCustom) {
        const custom = (invoiceConfig.footerColumnsText ?? [])
            .map((col) =>
                String(col ?? '')
                    .replace(/\r\n/g, '\n')
                    .split('\n')
                    .map((line) => line.trim())
                    .filter(Boolean),
            )
            .filter((col) => col.length > 0);
        if (custom.length) return { columns: custom };
    }

    const companyColumn = [
        t(company?.name),
        // Zeilenumbrueche UND Kommas trennen — der Betreiber bestimmt die
        // Zeilen im Anschrift-Feld selbst (Strasse allein, PLZ + Ort zusammen).
        // Dieselbe Zerlegung wie parseAddressLine der E-Rechnung.
        ...t(company?.address)
            .split(/\r?\n|,/)
            .map((part) => part.trim())
            .filter(Boolean),
    ].filter(Boolean);

    const contactColumn = [
        t(invoiceConfig.phone) && `Tel. ${t(invoiceConfig.phone)}`,
        t(invoiceConfig.email) && `E-Mail ${t(invoiceConfig.email)}`,
        t(invoiceConfig.website) && `Web ${t(invoiceConfig.website)}`,
    ].filter(Boolean) as string[];

    const directors = (invoiceConfig.managingDirectors || []).map((d) => d.trim()).filter(Boolean);
    const legalColumn = [
        t(invoiceConfig.registerCourt),
        t(invoiceConfig.registerNumber) && `HR-Nr. ${t(invoiceConfig.registerNumber)}`,
        t(invoiceConfig.vatId) && `USt.-ID ${t(invoiceConfig.vatId)}`,
        t(invoiceConfig.taxNumber) && `Steuer-Nr. ${t(invoiceConfig.taxNumber)}`,
        directors.length > 0 && `Geschäftsführung ${directors.join(', ')}`,
    ].filter(Boolean) as string[];

    const bankColumn = [
        t(invoiceConfig.bankName) && `Bank ${t(invoiceConfig.bankName)}`,
        t(invoiceConfig.iban) && `IBAN ${t(invoiceConfig.iban)}`,
        t(invoiceConfig.bic) && `BIC ${t(invoiceConfig.bic)}`,
    ].filter(Boolean) as string[];

    // Ohne strukturierte Angaben keine Spalten: die Firmenspalte allein waere
    // nur eine Wiederholung des Briefkopfs, waehrend der freie Fusszeilentext
    // verschwaende.
    if (!contactColumn.length && !legalColumn.length && !bankColumn.length) return undefined;

    // Rechtliches ist die Flex-Spalte: ihre Geschaeftsfuehrungszeile bricht
    // sauber um, waehrend Firmenname und IBAN ungebrochen bleiben muessen.
    // Der Index wird NACH dem Ausfiltern leerer Spalten bestimmt.
    const tagged = [
        { lines: companyColumn, flex: false },
        { lines: contactColumn, flex: false },
        { lines: legalColumn, flex: true },
        { lines: bankColumn, flex: false },
    ].filter((col) => col.lines.length > 0);
    if (!tagged.length) return undefined;

    const flexIdx = tagged.findIndex((col) => col.flex);
    return {
        columns: tagged.map((col) => col.lines),
        flexColumn: flexIdx >= 0 ? flexIdx : undefined,
    };
};

export const composeInvoiceFooterColumns = (
    company: CompanyBase | undefined,
    invoiceConfig: InvoiceConfig | undefined,
): string[][] | undefined => composeInvoiceFooter(company, invoiceConfig)?.columns;

export const buildInvoiceCompanyForPdf = (
    company: CompanyBase | undefined,
    quoteConfig: QuoteConfig | undefined,
    invoiceConfig: InvoiceConfig | undefined,
): CompanyInfo => {
    const base = invoiceConfig?.ownLetterhead
        ? buildFromLetterhead(company, invoiceConfig, quoteConfig?.footerFontSize)
        : buildCompanyForPdf(company, quoteConfig);
    const footer = composeInvoiceFooter(company, invoiceConfig);
    return {
        ...base,
        // Typografie bleibt global (dasselbe Muster wie footerFontSize):
        // der eigene Rechnungs-Briefkopf ersetzt TEXTE, nicht das
        // Erscheinungsbild — Angebot und Rechnung sollen gleich aussehen.
        letterheadLeftLineFontSize: quoteConfig?.letterheadLeftLineFontSize,
        footerColumns: footer?.columns,
        footerFlexColumn: footer?.flexColumn,
    };
};
